import {
  InformationPanel,
  MFAActions,
  Selectors,
  openURL,
  useWidgetDispatch,
  useWidgetState,
} from '@eCommerce/frontend-legacy-shared-library-native';
import {useDispatch, useSelector} from 'Provider';
import {View} from 'native-base';
import React, {Fragment, useCallback, useState} from 'react';
import {Platform} from 'react-native';

import {GatedModulePropTypes} from 'components/GatedProfilePanels';
import MFAWebViewModal from 'containers/MFAWebViewModal';
import {useMFAStepUp} from 'hooks/useMFAStepUp';
import {getMFAPreferenceURL} from 'models/MFA';
import {AuthActions, LogoutMsg} from 'store/auth/auth.reducer';
import {isWebViewSelector} from 'store/content';
import {webViewQueryParam} from 'utils';
import {getSigninUrl} from 'utils/signin';

export interface MFAGateProps {
  forwardUrl: string;
  children: React.ReactNode;
  GatedComponent: React.FC<GatedModulePropTypes>;
  forceLoading?: boolean;
}
const MFAGate: React.FC<MFAGateProps> = ({
  forwardUrl,
  children,
  GatedComponent,
  forceLoading = false,
}) => {
  const {
    mfaOptinStatus,
    isMFATokenValidationSuccess,
    isMFAStepUpLoading,
    triggerStepup,
  } = useMFAStepUp({
    forwardUrl,
  });
  const isRaaSToken = useWidgetState(Selectors.isRaaSTokenSelector) as boolean;
  const isValidateTokenInitial = useWidgetState(
    Selectors.isValidateTokenInitialSelector
  ) as boolean;
  const dispatch = useDispatch();
  const [isMFAWebViewVisible, setIsMFAWebViewVisible] = useState(false);
  const isWeb = Platform.OS === 'web';
  const widgetDispatch = useWidgetDispatch();
  const isWebView = useSelector(isWebViewSelector);

  const onSignInPress = useCallback(() => {
    const signinUrl = getSigninUrl(`forwardUrl=${forwardUrl}`);
    const _forwardUrl =
      signinUrl === 'LOCAL_SIGNIN_ROUTE' ? forwardUrl : signinUrl;

    dispatch(
      AuthActions.logout({
        msg: LogoutMsg.LOGOUT,
        forwardUrl: encodeURIComponent(_forwardUrl),
      })
    );
  }, [dispatch, forwardUrl]);

  const launchMFA = () => {
    widgetDispatch(MFAActions.clearValidateTokenState());
    setIsMFAWebViewVisible(true);
  };

  const onTriggerStepup = isWeb ? triggerStepup : launchMFA;

  const onOptin = () => {
    if (isWeb) {
      let mfaUrl = getMFAPreferenceURL(forwardUrl);
      if (isWebView) {
        mfaUrl += '&' + webViewQueryParam;
      }
      openURL(mfaUrl);
    } else {
      launchMFA();
    }
  };

  if (!isRaaSToken && isWeb && !isWebView) {
    return (
      <InformationPanel
        isSVG={false}
        iconName="exclamation-circle"
        iconSize={{height: 72, width: 72}}
        titleText="Your Session Has Expired"
        upperDescriptionText="Please sign in again to view your account."
        ctaText="Sign-in"
        onCTAPress={onSignInPress}
        hasShadow
        iconIsLight
        iconColor="#D60000"
      />
    );
  }

  const renderChildren = () => {
    if (mfaOptinStatus && !forceLoading) {
      if (isMFATokenValidationSuccess || !isValidateTokenInitial) {
        return <>{children}</>;
      }
    }

    return (
      <Fragment>
        {/**
         * TODO: this is just a dummy view to help the tests pass
         * fix this.
         */}
        <View
          testID={
            isMFAStepUpLoading && isValidateTokenInitial
              ? 'mfa-loading'
              : undefined
          }
        />
        <GatedComponent
          isLoading={
            (isMFAStepUpLoading && isValidateTokenInitial) || forceLoading
          }
          mfaOptinStatus={mfaOptinStatus}
          onPrimaryPress={mfaOptinStatus ? onTriggerStepup : onOptin}
        />
      </Fragment>
    );
  };

  return (
    <>
      {renderChildren()}
      <MFAWebViewModal
        onClose={() => {
          setIsMFAWebViewVisible(false);
        }}
        mfaOptinStatus={mfaOptinStatus}
        isModalVisible={isMFAWebViewVisible}
      />
    </>
  );
};

export default MFAGate;
